/**
 * TanStack Query keys.
 *
 * Centralised so that invalidation is reliable: a mutation can invalidate
 * `queryKeys.playlists.all` and be certain it catches every playlist query on
 * both platforms, without either app inventing its own key strings.
 *
 * Keys are hierarchical — `['playlists']` is a prefix of
 * `['playlists', 'detail', id]`, so invalidating the former clears the latter.
 */

export const queryKeys = {
  /**
   * Public, user-independent server state. Deliberately absent from
   * PRIVATE_QUERY_KEY_PREFIXES: signing out must not discard the version
   * policy, or the update screen would stop working on the sign-in screen.
   */
  system: {
    all: ['system'],
    appVersion: (params) => ['system', 'app-version', params ?? {}],
  },

  auth: {
    all: ['auth'],
    me: () => ['auth', 'me'],
  },

  billing: {
    all: ['billing'],
    status: () => ['billing', 'status'],
    limits: () => ['billing', 'limits'],
    usage: () => ['billing', 'usage'],
    management: () => ['billing', 'management'],
  },

  songs: {
    all: ['songs'],
    lists: () => ['songs', 'list'],
    list: (filters) => ['songs', 'list', filters ?? {}],
    search: (filters) => ['songs', 'search', filters ?? {}],
    details: () => ['songs', 'detail'],
    detail: (songId) => ['songs', 'detail', songId],
    arrangements: (songId) => ['songs', 'detail', songId, 'arrangements'],
    mine: (filters) => ['songs', 'mine', filters ?? {}],
  },

  arrangements: {
    all: ['arrangements'],
    details: () => ['arrangements', 'detail'],
    detail: (arrangementId) => ['arrangements', 'detail', arrangementId],
    revisions: (arrangementId) => ['arrangements', 'detail', arrangementId, 'revisions'],
  },

  favorites: {
    all: ['favorites'],
    list: (filters) => ['favorites', 'list', filters ?? {}],
    check: (arrangementId) => ['favorites', 'check', arrangementId],
  },

  playlists: {
    all: ['playlists'],
    lists: () => ['playlists', 'list'],
    list: (filters) => ['playlists', 'list', filters ?? {}],
    details: () => ['playlists', 'detail'],
    detail: (playlistId) => ['playlists', 'detail', playlistId],
    items: (playlistId) => ['playlists', 'detail', playlistId, 'items'],
  },

  history: {
    all: ['history'],
    recent: (filters) => ['history', 'recent', filters ?? {}],
  },

  chords: {
    all: ['chords'],
    list: (filters) => ['chords', 'list', filters ?? {}],
    detail: (chordId) => ['chords', 'detail', chordId],
    resolve: (symbol, instrument) => ['chords', 'resolve', symbol, instrument ?? 'guitar'],
    suggestions: (query) => ['chords', 'suggestions', query ?? {}],
  },

  learn: {
    all: ['learn'],
    curriculum: () => ['learn', 'curriculum'],
    profile: () => ['learn', 'profile'],
    badges: () => ['learn', 'badges'],
    lessons: () => ['learn', 'lesson'],
    lesson: (lessonId) => ['learn', 'lesson', lessonId],
    daily: () => ['learn', 'daily'],
    listening: () => ['learn', 'listening'],
  },

  downloads: {
    all: ['downloads'],
    list: () => ['downloads', 'list'],
  },

  reports: {
    all: ['reports'],
    mine: () => ['reports', 'mine'],
  },

  admin: {
    all: ['admin'],
    dashboard: () => ['admin', 'dashboard'],
    appVersionPolicy: () => ['admin', 'app-version-policy'],
    users: (filters) => ['admin', 'users', filters ?? {}],
    user: (userId) => ['admin', 'users', 'detail', userId],
    subscriptions: (filters) => ['admin', 'subscriptions', filters ?? {}],
    subscription: (userId) => ['admin', 'subscriptions', 'detail', userId],
    songs: (filters) => ['admin', 'songs', filters ?? {}],
    arrangements: (filters) => ['admin', 'arrangements', filters ?? {}],
    arrangement: (arrangementId) => ['admin', 'arrangements', 'detail', arrangementId],
    chords: (filters) => ['admin', 'chords', filters ?? {}],
    reports: (filters) => ['admin', 'reports', filters ?? {}],
    auditLogs: (filters) => ['admin', 'audit-logs', filters ?? {}],
    learnUnits: (filters) => ['admin', 'learn', 'units', filters ?? {}],
    learnLessons: (filters) => ['admin', 'learn', 'lessons', filters ?? {}],
    learnLesson: (lessonId) => ['admin', 'learn', 'lessons', 'detail', lessonId],
  },
};

/**
 * Query key prefixes holding user-scoped private data.
 *
 * On logout every one of these is removed from the cache so the next user can
 * never see the previous user's library. Public catalog data may remain.
 */
export const PRIVATE_QUERY_KEY_PREFIXES = Object.freeze([
  ['auth'],
  ['billing'],
  ['favorites'],
  ['playlists'],
  ['history'],
  // Learn holds XP, streaks and completion — as user-scoped as a library is.
  ['learn'],
  ['downloads'],
  ['reports'],
  ['admin'],
  ['songs', 'mine'],
]);

/**
 * Clears every private cache entry.
 * Call on sign-out and when switching accounts.
 */
export function clearPrivateQueries(queryClient) {
  for (const prefix of PRIVATE_QUERY_KEY_PREFIXES) {
    queryClient.removeQueries({ queryKey: prefix });
  }
}
